import {
  useMutation,
  useQuery,
  useQueryClient,
  type UseMutationResult,
  type UseQueryResult,
} from "@tanstack/react-query";

import {
  createSite,
  deleteSite,
  getSite,
  listSites,
  updateSite,
  type Site,
  type SiteCreate,
  type SitesListParams,
  type SiteUpdate,
} from "@/lib/api";

export const sitesKeys = {
  all: ["sites"] as const,
  list: (params?: SitesListParams) => [...sitesKeys.all, "list", params] as const,
  detail: (id: number) => [...sitesKeys.all, "detail", id] as const,
};

export function useSites(
  params?: SitesListParams,
): UseQueryResult<Site[], Error> {
  return useQuery({
    queryKey: sitesKeys.list(params),
    queryFn: () => listSites(params),
  });
}

export function useSite(id: number | null): UseQueryResult<Site, Error> {
  return useQuery({
    queryKey: sitesKeys.detail(id ?? 0),
    queryFn: () => getSite(id as number),
    enabled: id !== null && id !== undefined && id > 0,
  });
}

export function useCreateSite(): UseMutationResult<Site, Error, SiteCreate> {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: SiteCreate) => createSite(body),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: sitesKeys.all });
    },
  });
}

export function useUpdateSite(): UseMutationResult<
  Site,
  Error,
  { id: number; body: SiteUpdate }
> {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, body }) => updateSite(id, body),
    onSuccess: (_data, variables) => {
      qc.invalidateQueries({ queryKey: sitesKeys.all });
      qc.invalidateQueries({ queryKey: sitesKeys.detail(variables.id) });
    },
  });
}

export function useDeleteSite(): UseMutationResult<void, Error, number> {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: number) => deleteSite(id),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: sitesKeys.all });
    },
  });
}
